import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "react-oidc-context";
import { useRolesEfectivos, useTieneRol } from "@/lib/sesion";
import {
  AlertCircle,
  Mail,
  Inbox,
  Cpu,
  Shield,
  Clock,
  FileWarning,
  Hourglass,
  ChevronRight,
} from "lucide-react";

interface MeResponse {
  usuario: {
    id: string | null;
    correo: string;
    nombre: string;
    apellidoPaterno: string;
    apellidoMaterno: string | null;
    tipo: string | null;
    estatus: string;
  };
  rolesKeycloak: string[];
  preferred_username: string;
}

export function HomePage() {
  const auth = useAuth();
  const { data, isLoading, error } = useQuery({
    queryKey: ["me"],
    queryFn: async () => (await api.get<MeResponse>("/api/me")).data,
    enabled: auth.isAuthenticated,
  });
  // Qué secciones se dibujan lo deciden los roles EFECTIVOS, no los de
  // `/api/me`: ese endpoint lee `realm_access` del token, que durante una
  // suplantación sigue siendo el del administrador. Dibujar con ellos montaba
  // paneles que el usuario suplantado no puede consultar y disparaba peticiones
  // condenadas al 403.
  const roles = useRolesEfectivos();

  return (
    <div className="space-y-6">
      <header>
        <h1 className="text-2xl font-semibold text-fg">Bienvenido a SICEN</h1>
        <p className="text-sm text-fg-muted">
          Plataforma de gestión de certificaciones de NPMX. Tu vista se ajustará a tu rol y contexto.
        </p>
      </header>

      <section className="rounded-xl border border-edge/70 bg-surface p-6 shadow-card">
        <h2 className="mb-3 text-lg font-semibold">Tu sesión</h2>
        {isLoading && <p className="text-sm text-fg-subtle">Cargando perfil…</p>}
        {error && (
          <div className="flex items-start gap-2 rounded-md bg-estado-warn-bg p-3 text-sm text-estado-warn">
            <AlertCircle className="mt-0.5 h-4 w-4" />
            <div>
              No fue posible obtener tu perfil. Intenta de nuevo en unos momentos; si el
              problema persiste, contacta al administrador del sistema.
            </div>
          </div>
        )}
        {data && (
          <dl className="grid gap-3 text-sm md:grid-cols-2">
            <Field label="Correo" value={data.usuario.correo} />
            <Field label="Nombre" value={`${data.usuario.nombre} ${data.usuario.apellidoPaterno ?? ""}`} />
            <Field label="Tipo" value={data.usuario.tipo ?? "Pendiente de activación"} />
            <Field label="Estatus" value={data.usuario.estatus} />
            <Field label="Roles" value={roles.filter((r) => !["offline_access", "uma_authorization", "default-roles-sicen"].includes(r)).join(", ") || "—"} colSpan />
          </dl>
        )}
      </section>

      {hasAnyRole(roles, ["GERENTE_CALIDAD", "AUXILIAR_CALIDAD", "ADMIN_SICEN"]) && (
        <DashboardCalidad />
      )}

      {hasAnyRole(roles, [
        "RESPONSABLE_TELECOM",
        "EVALUADOR_TECNICO",
        "COORDINADOR_TECNICO",
        "AUDITOR_INTERNO",
        "TRAMITADOR",
        "TITULAR",
        "USUARIO_GESTOR",
        "REPRESENTANTE_LEGAL",
        "FACTURACION",
        "ADMIN_SICEN",
      ]) && (
        <DashboardSectores />
      )}

      {roles.includes("ADMIN_SICEN") && (
        <section className="rounded-xl border border-edge/70 bg-surface p-6 shadow-card">
          <h2 className="mb-3 text-lg font-semibold">Administración</h2>
          <Link
            to="/admin/plantillas"
            className="inline-flex items-center gap-2 rounded border border-primary-strong/40 px-3 py-2 text-sm font-medium text-primary-strong hover:bg-primary hover:text-white"
          >
            <Mail className="h-4 w-4" /> Plantillas de notificación
          </Link>
        </section>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Solicitudes por sector (conteo "en proceso" + acceso directo)
// ---------------------------------------------------------------------------

interface SectorInfo {
  codigo: string;
  nombre: string;
  Icono: React.ComponentType<{ className?: string }>;
  lista: string;
}

/** Sectores con módulo operativo. Al agregar uno nuevo, basta sumarlo aquí. */
const SECTORES_OPERATIVOS: SectorInfo[] = [
  { codigo: "telecom", nombre: "Telecomunicaciones", Icono: Cpu, lista: "/telecom" },
  { codigo: "seguridad", nombre: "Seguridad", Icono: Shield, lista: "/seguridad" },
];

function DashboardSectores() {
  return (
    <section className="space-y-3">
      <header className="flex items-baseline justify-between">
        <h2 className="text-lg font-semibold text-fg">Solicitudes por sector</h2>
      </header>
      <div className="grid gap-3 md:grid-cols-3">
        {SECTORES_OPERATIVOS.map((s) => (
          <SectorTile key={s.codigo} sector={s} />
        ))}
      </div>
    </section>
  );
}

function SectorTile({ sector }: { sector: SectorInfo }) {
  const { data, isLoading } = useQuery<{ enProceso: number }>({
    // Misma queryKey que la campana del topbar → una sola petición compartida.
    queryKey: [`sicen-contadores-${sector.codigo}`],
    queryFn: async () =>
      (await api.get<{ enProceso: number }>(`/api/sector/${sector.codigo}/solicitudes/contadores`)).data,
    staleTime: 30_000,
    refetchInterval: 60_000,
  });
  const enProceso = data?.enProceso ?? 0;
  const Icono = sector.Icono;
  return (
    <Link
      to={`${sector.lista}?filtro=en-proceso`}
      className="flex flex-col rounded-xl border border-edge/70 bg-surface shadow-card transition hover:border-primary-strong hover:shadow-card-hover"
    >
      <div className="flex items-center justify-between gap-2 border-b border-edge px-4 py-3">
        <div className="flex items-center gap-2 text-xs uppercase tracking-wide text-fg-subtle">
          <Icono className="h-5 w-5 text-primary-strong" />
          {sector.nombre}
        </div>
        <span className="num-tabular text-2xl font-semibold text-primary-strong">
          {isLoading ? "…" : enProceso}
        </span>
      </div>
      <div className="flex items-center justify-between px-4 py-2 text-xs text-fg-subtle">
        <span>Solicitudes en proceso</span>
        <span className="inline-flex items-center gap-1 font-medium text-primary-strong">
          Ver <ChevronRight className="h-3 w-3" />
        </span>
      </div>
    </Link>
  );
}

// ---------------------------------------------------------------------------
// Dashboard de alertas para roles de Calidad
// ---------------------------------------------------------------------------

interface ActivoIncompleto {
  id: string;
  folioRegistro: string | null;
  razonSocial: string;
  rfc: string;
  estatus: string;
  totalFaltantes: number;
}

interface EnRevisionItem {
  id: string;
  folioRegistro: string | null;
  razonSocial: string;
  rfc: string;
  estatus: string;
  fechaEnvioRevision: string | null;
  diasEnRevision: number;
}

interface DashboardResponse {
  activosConDocsIncompletos: ActivoIncompleto[];
  atorados: EnRevisionItem[];
  esperandoCliente: EnRevisionItem[];
  totales: {
    activosConDocsIncompletos: number;
    atorados: number;
    esperandoCliente: number;
  };
}

function DashboardCalidad() {
  // El panel ya se monta sólo con el rol, pero la consulta se guarda a sí misma:
  // así ninguna llamada sale sin la facultad que el backend va a exigir.
  const puedeConsultar = useTieneRol("GERENTE_CALIDAD", "AUXILIAR_CALIDAD", "ADMIN_SICEN");
  const { data, isLoading, error } = useQuery({
    queryKey: ["dashboard-calidad"],
    queryFn: async () => (await api.get<DashboardResponse>("/api/registros-legales/dashboard-calidad")).data,
    staleTime: 60_000,
    enabled: puedeConsultar,
  });
  if (!puedeConsultar) return null;

  return (
    <section className="space-y-3">
      <header className="flex items-baseline justify-between">
        <h2 className="text-lg font-semibold text-fg">Registro Legal — alertas</h2>
        <Link
          to="/calidad"
          className="inline-flex items-center gap-1 text-xs text-fg-muted hover:text-primary-strong"
        >
          <Inbox className="h-3 w-3" /> Bandejas operativas
        </Link>
      </header>
      {isLoading && <p className="rounded-xl border border-edge/70 bg-surface p-4 text-sm text-fg-subtle">Cargando alertas…</p>}
      {error && (
        <p className="flex items-start gap-2 rounded-lg border border-estado-warn/25 bg-estado-warn-bg p-3 text-sm text-estado-warn">
          <AlertCircle className="mt-0.5 h-4 w-4" /> No fue posible cargar las alertas de Registro Legal.
        </p>
      )}
      {data && (
        <div className="grid gap-3 md:grid-cols-3">
          <DashboardTarjeta
            icon={<FileWarning className="h-5 w-5 text-estado-err" />}
            title="Activos con docs incompletos"
            tone="rose"
            total={data.totales.activosConDocsIncompletos}
            empty="Sin casos críticos. Todos los RLs vigentes están completos."
            verTodos={{
              to: "/tramites-legales?tab=vigentes",
              label: "Ver vigentes",
            }}
            items={data.activosConDocsIncompletos.map((r) => ({
              key: r.id,
              to: `/registros-legales/${r.id}`,
              primary: r.razonSocial,
              secondary: r.folioRegistro ?? r.rfc,
              chip: <Chip tone="rose">{r.totalFaltantes} faltantes</Chip>,
            }))}
          />
          <DashboardTarjeta
            icon={<Clock className="h-5 w-5 text-estado-warn" />}
            title="Atorados >5 días"
            tone="amber"
            total={data.totales.atorados}
            empty="Todo en tiempo. Sin casos atorados."
            verTodos={{
              to: "/tramites-legales?tab=revision&sort=fechaEnvioRevision%2Casc",
              label: "Ver todos",
            }}
            items={data.atorados.map((r) => ({
              key: r.id,
              to: `/registros-legales/${r.id}`,
              primary: r.razonSocial,
              secondary: r.estatus,
              chip: <Chip tone="amber">{r.diasEnRevision}d</Chip>,
            }))}
          />
          <DashboardTarjeta
            icon={<Hourglass className="h-5 w-5 text-estado-info" />}
            title="Esperando cliente"
            tone="blue"
            total={data.totales.esperandoCliente}
            empty="Ningún caso pendiente de respuesta del cliente."
            verTodos={{
              to: "/tramites-legales?tab=observados&sort=fechaEnvioRevision%2Casc",
              label: "Ver observados",
            }}
            items={data.esperandoCliente.map((r) => ({
              key: r.id,
              to: `/registros-legales/${r.id}`,
              primary: r.razonSocial,
              secondary: r.estatus,
              chip: <Chip tone="blue">{r.diasEnRevision}d</Chip>,
            }))}
          />
        </div>
      )}
    </section>
  );
}

interface TarjetaItem {
  key: string;
  to: string;
  primary: string;
  secondary: string;
  chip: React.ReactNode;
}

function DashboardTarjeta({
  icon,
  title,
  tone,
  total,
  items,
  empty,
  verTodos,
}: {
  icon: React.ReactNode;
  title: string;
  tone: "rose" | "amber" | "blue";
  total: number;
  items: TarjetaItem[];
  empty: string;
  verTodos: { to: string; label: string };
}) {
  const totalCls = {
    rose: "text-estado-err",
    amber: "text-estado-warn",
    blue: "text-estado-info",
  }[tone];
  return (
    <div className="flex flex-col rounded-xl border border-edge/70 bg-surface shadow-card">
      <div className="flex items-start justify-between gap-2 border-b border-edge px-4 py-3">
        <div className="flex items-center gap-2 text-xs uppercase tracking-wide text-fg-subtle">
          {icon}
          {title}
        </div>
        <span className={`num-tabular text-2xl font-semibold ${totalCls}`}>{total}</span>
      </div>
      <div className="flex-1 divide-y divide-edge text-sm">
        {items.length === 0 ? (
          <p className="px-4 py-6 text-xs text-fg-subtle">{empty}</p>
        ) : (
          items.slice(0, 5).map((it) => (
            <Link
              key={it.key}
              to={it.to}
              className="flex items-center justify-between gap-3 px-4 py-2 hover:bg-surface-2"
            >
              <div className="min-w-0 flex-1">
                <div className="truncate font-medium text-fg">{it.primary}</div>
                <div className="truncate text-xs text-fg-subtle">{it.secondary}</div>
              </div>
              {it.chip}
            </Link>
          ))
        )}
      </div>
      <div className="border-t border-edge px-4 py-2 text-right">
        <Link
          to={verTodos.to}
          className="inline-flex items-center gap-1 text-xs font-medium text-primary-strong hover:underline"
        >
          {verTodos.label} <ChevronRight className="h-3 w-3" />
        </Link>
      </div>
    </div>
  );
}

function Chip({ tone, children }: { tone: "rose" | "amber" | "blue"; children: React.ReactNode }) {
  const cls = {
    rose: "bg-estado-err-bg text-estado-err",
    amber: "bg-estado-warn-bg text-estado-warn",
    blue: "bg-estado-info-bg text-estado-info",
  }[tone];
  return (
    <span className={`shrink-0 rounded-full px-2 py-0.5 text-xs font-medium ${cls}`}>{children}</span>
  );
}

function hasAnyRole(userRoles: string[], roles: string[]): boolean {
  return roles.some((r) => userRoles.includes(r));
}

function Field({ label, value, colSpan }: { label: string; value: string; colSpan?: boolean }) {
  return (
    <div className={colSpan ? "md:col-span-2" : undefined}>
      <dt className="text-xs uppercase tracking-wide text-fg-subtle">{label}</dt>
      <dd className="font-medium">{value}</dd>
    </div>
  );
}
